import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

interface AuthContextType {
  user: UserInfo | null;
  loading: boolean;
  login: (data: UserInfo) => void;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<UserInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const baseUrl = process.env.REACT_APP_API_URL;

  const refresh = useCallback(async () => {
    try {
      const response = await fetch(`${baseUrl}/userinfo`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setUser(data);
      } else {
        setUser(null);
      }
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [baseUrl]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = useCallback(
    (data: UserInfo) => {
      setUser(data);
      navigate("/app");
    },
    [navigate]
  );

  const logout = useCallback(async () => {
    try {
      await fetch(`${baseUrl}/platform/logout`, { method: "POST", credentials: "include" });
    } catch {
      // best-effort — clear session regardless
    }
    setUser(null);
    navigate("/", { replace: true });
  }, [navigate, baseUrl]);

  const value = useMemo(
    () => ({ user, loading, login, logout, refresh }),
    [user, loading, login, logout, refresh]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
